import { IconButton, Stack, Typography } from '@mui/material';
import { SchoolUserType, StaffSearchResult, useGetStaffQuery } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { getCombinedRowsFromSearchResults } from '@schooly/components/filters';
import { useFlag } from '@schooly/hooks/use-flag';
import { DeleteIcon, PlusIcon, SimpleButton } from '@schooly/style';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';

import { MessageForm } from '../../../../context/messages/MessageContext';
import {
  RecipientsByIndividualFormRef,
  RecipientsByIndividualSelectMultiple,
} from '../MessagesCreateRecipientsSelect/RecipientsByIndividualSelect';
import { RecipientsFormContainer } from './ModalCreateModalRecipients.styled';

export interface StaffByIndividualFormProps {
  error: boolean;
  title: string;
  schoolId: string;
  isInitOpen?: boolean;
  onClose?: (close: () => void) => void;
  validate?: (v: string[]) => string | undefined;
  buttonTextId: string;
}

const type = 'staff' as SchoolUserType;

export const StaffByIndividualForm = forwardRef<
  RecipientsByIndividualFormRef,
  StaffByIndividualFormProps
>(({ title, error, schoolId, isInitOpen, onClose, validate, buttonTextId }, ref) => {
  const { formatMessage } = useIntl();
  const [isOpen, open, close] = useFlag(isInitOpen);
  const { control } = useFormContext<MessageForm>();

  const { permissions } = useAuth();
  const [queryEnabled, setQueryEnabled] = useState(false);
  const canRequestData = permissions.includes(`${type}_viewer`);

  const { data, isLoading, hasNextPage, params, setParams, isFetchingNextPage, fetchNextPage } =
    useGetStaffQuery(
      {
        schoolId,
        query: '',
        filters: {},
      },
      { enabled: queryEnabled && canRequestData },
    );

  const entries = useMemo(
    () =>
      getCombinedRowsFromSearchResults(
        data?.pages.reduce((prev, curr) => [...prev, ...curr.results], [] as StaffSearchResult[]) ??
          [],
      ),
    [data?.pages],
  );

  const handleChangeQuery = useCallback(
    (query: string) => {
      setParams((p) => ({ ...p, query }));
    },
    [setParams],
  );

  const handleClose = onClose ? () => onClose(close) : close;

  useImperativeHandle(ref, () => ({
    openRecipientsForm: open,
  }));

  return (
    <>
      {isOpen ? (
        <RecipientsFormContainer error={error} gap={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h4">{title}</Typography>
            <IconButton inverse onClick={handleClose}>
              <DeleteIcon />
            </IconButton>
          </Stack>
          <Controller
            name="staff_ids"
            control={control}
            rules={{ validate }}
            render={({ field }) => (
              <RecipientsByIndividualSelectMultiple
                data={entries}
                query={params.query || ''}
                hasNextPage={!!hasNextPage}
                fetchNextPage={fetchNextPage}
                isFetchingNextPage={isFetchingNextPage || isLoading}
                type="staff"
                schoolId={schoolId}
                onSelectUserId={(v) => {
                  field.value.includes(v)
                    ? field.onChange(field.value.filter((e) => e !== v))
                    : field.onChange([...field.value, v]);
                  setParams((p) => ({ ...p, query: '' }));
                }}
                selectedIds={field.value}
                emptyPlaceholder={formatMessage({ id: 'messages-TypeName' })}
                name={field.name}
                onToggleDropdown={() => setQueryEnabled(true)}
                onChangeQuery={handleChangeQuery}
                renderRow={(user) => {
                  const relation = user.item.registration;
                  const value = 'job_title' in relation ? (relation.job_title as string) : '-';
                  return <Typography sx={{ flex: '1 1 50%' }}>{value}</Typography>;
                }}
              />
            )}
          ></Controller>
        </RecipientsFormContainer>
      ) : (
        <SimpleButton startIcon={<PlusIcon />} onClick={open} sx={{ alignSelf: 'flex-start' }}>
          {formatMessage({ id: buttonTextId })}
        </SimpleButton>
      )}
    </>
  );
});
